import { RootStore } from './index';
import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { LogsDto, TimestampField } from '../../dtos/logs.dto';
import { LogCriteria } from '../../dtos/search-criteria.dto';
import { AllowDropRuleAction, DirectionFilterNames, InsightsSubMenu, LogsType, TimeRange } from '../../utils/enums';
import { Column } from '../../model/table';
import { urlFilteringColumns } from '../features/reports-and-logs/virtualized-table/url-filtering-columns-config';
import { trafficColumns } from '../features/reports-and-logs/virtualized-table/traffic-columns-config';
import { calcActionDuration, CUSTOM_TIME_RANGE } from '../../utils/constants';
import { getStartAndEndDate } from '../../utils/datesUtils';
import logsService from '../../services/logs.service';
import { BaseStore } from './base.store';
import { SelectionOption } from '../shared/selection-button/selection-option';
import { ExportOptions } from '../features/reports-and-logs/logs/export/export-options';
import { threatColumns } from '../features/reports-and-logs/virtualized-table/threat-columns-config';
import { EventType } from 'src/dtos/analytics.dto';
import moment from 'moment/moment';

export class LogsStore extends BaseStore {
    public logs: Map<LogsType, LogsDto[]> = new Map([
        [LogsType.URL_FILTERING, []],
        [LogsType.TRAFFIC, []],
        [LogsType.THREAT, []]]);

    public searchCriteria: Record<LogsType, LogCriteria> = {
        [LogsType.URL_FILTERING]: {},
        [LogsType.TRAFFIC]: {},
        [LogsType.THREAT]: {}
    };

    public selectedLogsType: LogsType = LogsType.TRAFFIC;
    public columnConfigMap: Map<LogsType, Column<LogsDto>[]> = new Map([
        [LogsType.TRAFFIC, trafficColumns],
        [LogsType.URL_FILTERING, urlFilteringColumns],
        [LogsType.THREAT, threatColumns]]);

    public isLoadingByTypeMap: Map<LogsType, boolean> = new Map([
        [LogsType.URL_FILTERING, false],
        [LogsType.TRAFFIC, false],
        [LogsType.THREAT, false]]);

    public exportMethodByLogsType: Map<LogsType, SelectionOption> = new Map([
        [LogsType.TRAFFIC, ExportOptions[0]],
        [LogsType.URL_FILTERING, ExportOptions[0]],
        [LogsType.THREAT, ExportOptions[0]]]);

    public maxLogsForLocalDownload: number = 5000;
    public isInitialStateMap: Map<LogsType, boolean> = new Map([
        [LogsType.URL_FILTERING, true],
        [LogsType.TRAFFIC, true],
        [LogsType.THREAT, true]]
    );

    public activeSubMenu: InsightsSubMenu = InsightsSubMenu.REPORTS;

    constructor(rootStore: RootStore) {
        super(rootStore);
        this.reset();

        makeObservable(this, {
            logs: observable,
            searchCriteria: observable,
            selectedLogsType: observable,
            isLoadingByTypeMap: observable,
            exportMethodByLogsType: observable,
            isInitialStateMap: observable,
            activeSubMenu: observable,
            selectLogType: action,
            setTimeRange: action,
            setSearchCriteria: action,
            setExportMethod: action,
            setActiveSubMenu: action,
            logsByType: computed,
            columnConfigByType: computed,
            currentSearchCriteria: computed,
            exportMethod: computed,
            isInitialState: computed
        });
    }

    public async load(): Promise<void> {
        const startTime = performance.now();
        const selectedLogsType = this.selectedLogsType;
        try {
            runInAction(() => {
                this.isLoadingByTypeMap.set(selectedLogsType, true);
            });
            const customerId = this.rootStore.customerStore.customerId;
            const serviceId = this.rootStore.customerStore.selectedLocation?.serviceId;
            this.updateTimeCriteria();
            const res = await logsService.getLogs(customerId, serviceId!, selectedLogsType, this.currentSearchCriteria);
            this.sendEvent(EventType.LogsLoad, {
                logsType: this.selectedLogsType,
                filters: Object.entries(this.searchCriteria),
                actionDuration: calcActionDuration(startTime)
            });

            runInAction(() => {
                this.logs.set(selectedLogsType, this.convertResponse(res));
                this.isInitialStateMap.set(selectedLogsType, false);
                this.isLoadingByTypeMap.set(selectedLogsType, false);
            });
        }
        catch (e) {
            this.sendEvent(EventType.LogsLoad, {
                logLevel: 'error', actionDuration: calcActionDuration(startTime),
                error: e?.toString(), logsType: this.selectedLogsType, filters: this.searchCriteria,
            });
            this.isLoadingByTypeMap.set(selectedLogsType, false);
            throw e;
        }
    }

    private convertResponse(res: LogsDto[]) {
        return res?.length ? res.map(log => (
            {
                ...log,
                [TimestampField]: moment(log[TimestampField]).format('MMM DD, YYYY\u00A0\u00A0\u00A0\u00A0HH:mm:ss')
            }
        )) : res;
    }

    get logsByType(): LogsDto[] {
        return this.logs.get(this.selectedLogsType) || [];
    }

    get columnConfigByType(): Column<LogsDto>[] {
        return this.columnConfigMap.get(this.selectedLogsType) || [];
    }

    get exportMethod(): SelectionOption {
        return this.exportMethodByLogsType.get(this.selectedLogsType)!;
    }

    get isInitialState(): boolean {
        return this.isInitialStateMap.get(this.selectedLogsType) || false;
    }

    public setExportMethod(value: any) {
        const selectedOption = ExportOptions.find(e => e.label === value)!;
        this.exportMethodByLogsType.set(this.selectedLogsType, selectedOption);
    }

    public selectLogType(logType: LogsType) {
        if (this.selectedLogsType !== logType) {
            this.selectedLogsType = logType;
        }
    }

    public setSearchCriteria(searchCriteria: LogCriteria) {
        this.searchCriteria[this.selectedLogsType] = searchCriteria;
    }

    public setTimeRange(timeRange: string) {
        const currentSearchCriteria = this.searchCriteria[this.selectedLogsType];
        const [startTime, endTime] = getStartAndEndDate(timeRange);
        const updatedSearchCriteria: LogCriteria = {
            ...currentSearchCriteria,
            timeDetails: {
                timeRange: timeRange,
                startDate: startTime,
                endDate: endTime
            }
        };
        runInAction(() => {
            this.searchCriteria[this.selectedLogsType] = updatedSearchCriteria;
        });
    }

    private updateTimeCriteria() {
        const currentSearchCriteria = this.searchCriteria[this.selectedLogsType];
        if (currentSearchCriteria?.timeDetails?.timeRange &&
            currentSearchCriteria?.timeDetails?.timeRange !== CUSTOM_TIME_RANGE) {
            this.setTimeRange(currentSearchCriteria?.timeDetails?.timeRange);
        }
    }

    get currentSearchCriteria(): LogCriteria {
        return this.searchCriteria[this.selectedLogsType];
    }

    public downloadLogs(exportMethod: string, selectedLogsTypes: LogsType[], searchDtoByType: Record<LogsType, LogCriteria>): Promise<void> {
        const customerId = this.rootStore.customerStore.customerId;
        const serviceId = this.rootStore.customerStore.serviceId;

        // update start and end time according to timeRange
        Object.entries(searchDtoByType).forEach(([key, scd])  => {
            if (!scd.timeDetails?.timeRange || scd.timeDetails?.timeRange === CUSTOM_TIME_RANGE) return;
            const [startTime, endTime] = getStartAndEndDate(scd.timeDetails?.timeRange);
            searchDtoByType[key as LogsType] = {
                ...scd,
                timeDetails: {
                    ...scd.timeDetails,
                    startDate: startTime,
                    endDate: endTime
                }
            };
        });

        return logsService.downloadLogs(customerId, serviceId, exportMethod, selectedLogsTypes, searchDtoByType);
    }

    public setActiveSubMenu(activeSubMenu: InsightsSubMenu) {
        this.activeSubMenu = activeSubMenu;
    }

    public reset() {
        this.activeSubMenu = InsightsSubMenu.REPORTS;
        const initSearchCriteria = (): LogCriteria => ({
            'direction': DirectionFilterNames.ALL,
            'action': AllowDropRuleAction.ALL,
            timeDetails: {
                'timeRange': TimeRange.LAST_24_HOURS
            }
        });
        this.selectedLogsType = LogsType.TRAFFIC;
        this.searchCriteria[LogsType.TRAFFIC] = { ...initSearchCriteria(), rule: 'All' };
        this.searchCriteria[LogsType.URL_FILTERING] = initSearchCriteria();
        this.searchCriteria[LogsType.THREAT] = { ...initSearchCriteria(), threatTypes: [] };
        this.logs.set(LogsType.TRAFFIC, []);
        this.logs.set(LogsType.URL_FILTERING, []);
        this.logs.set(LogsType.THREAT, []);
        this.isInitialStateMap.set(LogsType.TRAFFIC, true);
        this.isInitialStateMap.set(LogsType.URL_FILTERING, true);
        this.isInitialStateMap.set(LogsType.THREAT, true);
    }

    public sendExportLogEvent(eventType: EventType, exportMethod?: string, filters?: Map<LogsType, LogCriteria>) {
        this.sendEvent(eventType, {
            exportMethod,
            ...filters ? { filters: Object.fromEntries(filters) } : {}
        });
    }
}
